import type React from 'react'
import { useCallback, useRef } from 'react'
import { useActiveObjectActions } from '@/components/app/editor/active-object-context'
import { useLiveSelectedShapes } from '@/hooks/use-live-selected-shapes'
import type { WireColor } from '@/api/types/color'
import { colorFillTargets } from '@/lib/colors/color-fill-targets'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { OnBatchShapesCommitted } from '@/lib/transforms/types/on-batch-shapes-committed'
import {
  type FillPaint,
  fillPaintForColor,
  fillPaintOf,
  retintFillPaint,
} from '@/lib/colors/fill-paint'

interface FillColorApplyOptions {
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  onBatchShapesCommitted?: OnBatchShapesCommitted
  palette: ReadonlyMap<number, WireColor>
}

/**
 * Applies a fill pick the way legacy `changeFillColor` does: it becomes the fill for the next drawn
 * shape, and also recolors every selected shape (group members included, open lines and photo
 * fills skipped) as one undo step. Tint drags preview live and commit once on release.
 */
export function useFillColorApply({
  shapeManagerRef,
  onBatchShapesCommitted,
  palette,
}: FillColorApplyOptions) {
  const selectedShapes = useLiveSelectedShapes(shapeManagerRef)
  const { setActiveObjects } = useActiveObjectActions()
  // Pre-drag fill per shape while a tint slider drag is in progress.
  const tintSessionRef = useRef<Map<string, FillPaint> | null>(null)

  const fillTargets = useCallback((): ShapeData[] => {
    const mgr = shapeManagerRef?.current

    if (!mgr) return []

    return colorFillTargets(selectedShapes, mgr.getShapes())
  }, [selectedShapes, shapeManagerRef])

  const commit = useCallback(
    (items: BatchShapeTransformItem[]) => {
      const mgr = shapeManagerRef?.current

      if (!mgr || items.length === 0) return

      onBatchShapesCommitted?.(items, 'update')

      const fresh = mgr.getShapes()
      const selectedIds = new Set(mgr.getSelectedShapeIds())

      setActiveObjects(fresh.filter((s) => selectedIds.has(s.id)))
    },
    [onBatchShapesCommitted, setActiveObjects, shapeManagerRef]
  )

  const applyFillColor = useCallback(
    (color: WireColor, tint: number) => {
      const mgr = shapeManagerRef?.current

      if (!mgr) return

      const paint = fillPaintForColor(color, tint)

      mgr.setLastUsedStyles(paint)
      tintSessionRef.current = null

      const items: BatchShapeTransformItem[] = []

      for (const shape of fillTargets()) {
        items.push({ shapeId: shape.id, before: fillPaintOf(shape), after: paint })
        mgr.updateShape(shape.id, paint)
      }

      commit(items)
    },
    [commit, fillTargets, shapeManagerRef]
  )

  const previewFillTint = useCallback(
    (tint: number) => {
      const mgr = shapeManagerRef?.current

      if (!mgr) return

      const lastUsed = retintFillPaint(mgr.getLastUsedStyles(), tint, palette)

      mgr.setLastUsedStyles(lastUsed ?? { fillTint: tint })

      if (!tintSessionRef.current) {
        tintSessionRef.current = new Map(fillTargets().map((s) => [s.id, fillPaintOf(s)]))
      }

      for (const [id, before] of tintSessionRef.current) {
        const next = retintFillPaint(before, tint, palette)

        if (next) mgr.updateShape(id, next)
      }
    },
    [fillTargets, palette, shapeManagerRef]
  )

  const commitFillTint = useCallback(
    (tint: number) => {
      previewFillTint(tint)

      const session = tintSessionRef.current

      tintSessionRef.current = null

      if (!session) return

      const items: BatchShapeTransformItem[] = []

      for (const [id, before] of session) {
        const after = retintFillPaint(before, tint, palette)

        if (after && after.fillTint !== before.fillTint) {
          items.push({ shapeId: id, before, after })
        }
      }

      commit(items)
    },
    [commit, palette, previewFillTint]
  )

  // The tint the field shows: the one tint every tintable target shares, or Mixed when they differ.
  // Targets with no palette fill (None) can't take a tint, so they don't count.
  const tintedTargets = fillTargets().filter((s) => s.fillColorId != null)
  const selectionTints = new Set(tintedTargets.map((s) => s.fillTint ?? 100))
  const isTintMixed = selectionTints.size > 1
  const selectionTint = selectionTints.size === 1 ? [...selectionTints][0] : null

  return { applyFillColor, previewFillTint, commitFillTint, selectionTint, isTintMixed }
}
